import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useAttachmentStaging } from '@/hooks/useAttachmentStaging';
import { useEntityComments } from '@/hooks/useEntityComments';
import { RequiredMark, GenericSelect, firstMissingRequiredMessage, type GenericFormField } from '@/styles/GenericForm';
import { GenericAttachmentsSection, GenericAttachment } from '@/styles/GenericAttachmentsSection';
import { GenericCommentsSection, TEAM_CLIENT_VENDOR_AUDIENCES } from '@/styles/GenericCommentsSection';
import { Input } from '@/components/ui/input';
import { DateInput } from '@/components/ui/date-input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { StandardButton, CloseButton, SaveChangesButton, CancelButton } from '@/styles/StandardButtons';
import { VendorField } from '@/components/shared/VendorField';
import type { LinkedRecordOption } from '@/components/shared/LinkedRecordsField';
import { PaymentHistorySection, toPaymentHistoryEntries, type PaymentHistoryEntry } from '@/components/shared/PaymentHistorySection';
import { LinkedDocsSection } from '@/components/shared/LinkedDocsSection';
import { fetchEntityList, fetchTeamMemberLookup } from '@/lib/entity-api';
import { LineItemsSection } from '@/styles/LineItemsSection';
import { billLinesTotal, documentTotals, formatMoney, lineAmount, lineCostCodeProblem, parseNonNegative, parsePercent, withDefaultCostCode, type LineItem } from '@/lib/line-items';
import { statusLabel } from '@/lib/status-label';
import { costCodeLabel, defaultCostCodeOf, operationalCodes, useCostCodes } from '@/lib/cost-codes';
import { searchCatalog, type CatalogSearchRow } from '@/features/cost-management/catalog/services/catalog-api';
import { lineFromCatalog, suggestionOf } from '@/features/cost-management/purchase-orders/lib/po-lines';
import { AddFromCatalogDialog } from '@/features/cost-management/purchase-orders/components/AddFromCatalogDialog';
import { fetchPurchaseOrderBilling, fetchRelatedPurchaseOrders, replaceBillPurchaseOrders } from '../services/billPurchaseOrdersService';
import {
  applyFullAmount, billingBase, billLineFromRow, billLinePayload, emptyBillLine, overBilledLines, pendingByLine, pendingComponents, splitProportionally, syncBillAmounts,
  type BilledShape, type PurchaseOrderBilling,
} from '../lib/bill-lines';
import '@/design-system/typography.css';
import '@/design-system/forms.css';
import { Bill, Project } from '../types';
import { BillsService, type BillLine } from '../services/billsService';
import { saveBillPayment } from '../services/billPaymentsService';
import { PaymentForm, type PaymentDraft } from './PaymentForm';
import { uploadBillAttachments, deleteBillAttachment } from '../store';
import { useProjectMembershipPrompt } from '@/hooks/useProjectMembershipPrompt';
import { useUser } from '@/contexts/UserContext';
import { useProject, getDefaultProjectIdForNewRecord } from '@/components/ProjectSelector';
import { colors } from '@/design-system/colors';

/** Prefill for a NEW bill that starts from another record (e.g. "Create Bill" on a purchase order); ignored when editing. */
export interface BillDefaults {
  project_id?: string;
  vendor_id?: string;
  vendor_name?: string;
  name?: string;
  description?: string;
  /**
   * The lines the new bill starts with. A bill made from a purchase order gets a COPY of its lines: `purchase_order_item_id` remembers which order line
   * each one covers and `bill_amount` is what is being billed on it (defaults to quantity x price).
   */
  lines?: Array<{ description: string; quantity: number; unit_price: number; unit?: string | null; cost_code_id?: string | null; catalog_item_id?: string | null; purchase_order_item_id?: string | null; bill_amount?: number }>;
  /** Tax % and Other Amount the new bill starts with (a bill made from a purchase order: its rate and what is still to bill of the other charges). The user adjusts them to the real invoice. */
  tax_pct?: number;
  other_amount?: number;
  /** The purchase orders the new bill starts linked to (they are offered even before the project's list loads). */
  purchase_orders?: Array<{ id: string; label: string }>;
}

interface BillFormProps {
  bill: Bill | null;
  projects: Project[];
  onClose: () => void;
  /** Creates or updates the bill itself (ONE request) and returns it. */
  onSave: (billData: any) => Promise<Bill | undefined>;
  /** Everything of this save is done (lines, files): refresh the list. Not awaited -- the form closes right away. */
  onSaved?: () => void;
  isOpen: boolean;
  defaults?: BillDefaults;
}

const NO_PROJECTS: Project[] = [];
const FORM_INPUT = 'font-form-input generic-form-field';
const DERIVED_STATUSES = ['paid', 'partially_paid'];
const STATUS_LABEL: Record<string, string> = { draft: 'Draft', open: 'Open', paid: 'Paid', partially_paid: 'Partially Paid', void: 'Void' };

const poLabel = (p: { po_number: string; title?: string | null }) => (p.title ? `${p.po_number} — ${p.title}` : p.po_number);
const poDetail = (p: { amount?: number | string | null; status?: string }) =>
  [p.status ? statusLabel(p.status) : '', p.amount != null ? `$${Number(p.amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : ''].filter(Boolean).join(' · ');

const todayIso = () => new Date().toISOString().split('T')[0];
const inDaysIso = (days: number) => new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
const normalize = (obj: Record<string, any>): Record<string, string> =>
  Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, v == null ? '' : String(v).trim()]));
/** The lines as compared for unsaved changes (the key is not part of what was saved). */
const serializeLines = (items: LineItem[]) => JSON.stringify(items.map(({ key: _key, ...rest }) => rest));

export function BillForm({ bill, projects = NO_PROJECTS, onClose, onSave, onSaved, isOpen, defaults }: BillFormProps) {
  const { companyId, user } = useUser();
  const { ensureMembers, prompt: membershipPrompt } = useProjectMembershipPrompt();
  const { selectedProject } = useProject();
  const [formData, setFormData] = useState<Record<string, any>>({});
  // The bill's lines (the shared Line Items Section), and its Tax (%) / Other Amount as text while typing
  const [lines, setLines] = useState<LineItem[]>([]);
  const [taxPct, setTaxPct] = useState('');
  const [otherAmount, setOtherAmount] = useState('');
  // what has been billed of each linked purchase order, by order id (the bill being edited left out) -- the base of Full Amount / Split / the reconciliation
  const [billingByPo, setBillingByPo] = useState<Record<string, PurchaseOrderBilling>>({});
  const [billingError, setBillingError] = useState<string | null>(null);
  // The payments recorded on THIS bill (bill_payments with its id; a voided payment is not money paid): reference, amount and date, read-only
  const [payments, setPayments] = useState<PaymentHistoryEntry[]>([]);
  const [catalogOpen, setCatalogOpen] = useState(false);
  // the company's vendors, for the vendor filter of Add from Catalog (read the first time it opens)
  const [catalogVendors, setCatalogVendors] = useState<Array<{ id: string; name: string }> | null>(null);
  // How the amount being billed is set: everything pending (full), a total spread in proportion (split), or typed line by line (manual).
  // Any hand edit of a line, the tax or the other amount makes it manual; the other two only fill the values in.
  type BillingMode = 'full' | 'split' | 'manual';
  const [billingMode, setBillingMode] = useState<BillingMode>('manual');
  const [splitAmount, setSplitAmount] = useState('');
  const [splitError, setSplitError] = useState<string | null>(null);
  const { codes: companyCostCodes } = useCostCodes(isOpen);
  // the company's default cost code, from its real records: every new line starts with it
  const defaultCostCodeId = defaultCostCodeOf(companyCostCodes)?.id ?? '';
  // a line added before the company's cost codes finished loading gets the default as soon as it is known (saved lines are never touched)
  useEffect(() => {
    setLines((previous) => withDefaultCostCode(previous, defaultCostCodeId));
  }, [defaultCostCodeId]);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // A bill created by a save that then failed on its lines / files: the retry UPDATES it, it never creates a second one.
  const [createdBillId, setCreatedBillId] = useState<string | null>(null);
  // Record Payment: for a bill that exists, the payment is saved at once; for a new bill it is a DRAFT kept here (nothing is
  // written) and recorded right after the bill is saved.
  const [showPayment, setShowPayment] = useState(false);
  const [paymentDraft, setPaymentDraft] = useState<PaymentDraft | null>(null);
  const [balance, setBalance] = useState<{ amountPaid: number; status: string } | null>(null);
  // The purchase order(s) the bill is linked to (read-only: a bill is linked when it is created from an order, never picked here)
  const [linkedPoIds, setLinkedPoIds] = useState<string[]>([]);
  const [linkedPoOptions, setLinkedPoOptions] = useState<LinkedRecordOption[]>([]);
  // the candidates for the PO dropdown: the purchase orders of the bill's project
  const [projectPoOptions, setProjectPoOptions] = useState<LinkedRecordOption[]>([]);
  const initialLinkedPoRef = useRef<string[]>([]);
  // links that already exist (or that the bill was created from): shown locked, they cannot be undone by a click on the checkbox
  const [lockedPoIds, setLockedPoIds] = useState<string[]>([]);
  const submitLock = useRef(false);
  const previousIsOpenRef = useRef(false);
  const initialValuesRef = useRef<Record<string, any>>({});
  const initialLinesRef = useRef<string>('[]');
  const initialChargesRef = useRef<string>('|');

  const activeBillId = bill?.id || createdBillId || undefined;
  const isEdit = !!activeBillId;
  const liveStatus = balance?.status ?? bill?.status;
  const statusLocked = !!bill?.id && !!liveStatus && DERIVED_STATUSES.includes(liveStatus);

  // Comments (generic architecture, entity_type='bill'): loaded and saved through the API, once the bill exists.
  const { comments, addComment, deleteComment } = useEntityComments('bill', activeBillId);

  // --- Attachments: staged while the bill does not exist yet, uploaded right away once it does --------------------
  const uploadFailures = useRef<string[]>([]);
  const uploadBillAttachmentsFn = useCallback(async (id: string, files: File[]): Promise<GenericAttachment[]> => {
    if (!companyId || !user?.id) {
      setError('Your session is not ready yet. Please try again in a moment.');
      return [];
    }
    // the files of one save go up side by side, not one after the other
    const results = await Promise.all(files.map(async (file) => {
      try {
        return (await uploadBillAttachments(id, [file], companyId, user.id))[0] ?? null;
      } catch (uploadError) {
        uploadFailures.current.push(uploadError instanceof Error ? uploadError.message : `"${file.name}" could not be uploaded`);
        return null;
      }
    }));
    const uploaded = results.filter((att): att is NonNullable<typeof att> => !!att);
    if (uploadFailures.current.length > 0) setError(uploadFailures.current.join('\n'));
    return uploaded as GenericAttachment[];
  }, [companyId, user?.id]);
  const deleteBillAttachmentFn = useCallback(async (attachmentId: string): Promise<boolean> => {
    if (!user) return false;
    return deleteBillAttachment(attachmentId, user.id);
  }, [user]);
  const {
    pendingFiles,
    uploadedFiles: uploadedAttachments,
    setUploadedFiles: setUploadedAttachments,
    handleUpload: handleFileUpload,
    handleRemovePending: handleRemovePendingFile,
    handleDelete: handleDeleteAttachment,
    resetPending: resetPendingFiles,
    uploadPending,
  } = useAttachmentStaging<GenericAttachment>({
    entityId: activeBillId,
    uploadFn: uploadBillAttachmentsFn,
    deleteFn: deleteBillAttachmentFn,
    newFilesPosition: 'prepend',
    onError: setError,
  });

  const initialValues = useMemo(() => {
    if (bill?.id) {
      return {
        bill_number: bill.number || '',
        project_id: bill.project_id || '',
        name: bill.name || '',
        vendor_id: bill.vendor_id || '',
        vendor_name: (bill as any).vendor ? ((bill as any).vendor.company_name || `${(bill as any).vendor.first_name || ''} ${(bill as any).vendor.last_name || ''}`.trim()) : '',
        issue_date: bill.issue_date || '',
        due_date: bill.due_date || '',
        type: bill.type || 'Bill',
        status: bill.status || 'open',
        cost_code_id: (bill as any).cost_code_id || '',
        description: bill.description || bill.notes || '',
      };
    }
    return {
      bill_number: '',
      project_id: defaults?.project_id || getDefaultProjectIdForNewRecord(selectedProject),
      name: defaults?.name ?? '',
      vendor_id: defaults?.vendor_id ?? '',
      vendor_name: defaults?.vendor_name ?? '',
      issue_date: todayIso(),
      due_date: inDaysIso(30),
      type: 'Bill',
      status: 'open',
      cost_code_id: '',
      description: defaults?.description ?? '',
    };
  }, [bill, selectedProject, defaults]);

  // Capture the initial values when the form opens (closed -> open), never while it is open.
  useEffect(() => {
    if (isOpen && !previousIsOpenRef.current) {
      initialValuesRef.current = normalize(initialValues);
      setFormData(initialValues);
      setError(null);
      setCreatedBillId(null);
      setLinkedPoIds([]);
      setLinkedPoOptions([]);
      initialLinkedPoRef.current = [];
      setLockedPoIds([]);
      if (bill?.id) {
        // the purchase orders this bill is linked to (read through the API; they may belong to another project than the bill's)
        fetchRelatedPurchaseOrders([bill.id])
          .then((byBill) => {
            const linked = byBill[bill.id] || [];
            initialLinkedPoRef.current = linked.map((p) => p.id);
            setLinkedPoIds(initialLinkedPoRef.current);
            setLockedPoIds(initialLinkedPoRef.current);
            setLinkedPoOptions(linked.map((p) => ({ id: p.id, label: poLabel(p), detail: poDetail(p) })));
          })
          .catch((e) => setError(e instanceof Error ? e.message : 'The purchase orders of this bill could not be loaded'));
      } else if (defaults?.purchase_orders?.length) {
        // "Create Bill" from a purchase order: it starts linked to it (saved with the bill)
        setLinkedPoIds(defaults.purchase_orders.map((p) => p.id));
        setLockedPoIds(defaults.purchase_orders.map((p) => p.id));
        setLinkedPoOptions(defaults.purchase_orders.map((p) => ({ id: p.id, label: p.label })));
      }
      setPaymentDraft(null);
      setShowPayment(false);
      setBalance(null);
      submitLock.current = false;
      uploadFailures.current = [];

      const items: LineItem[] = bill?.id && (bill as any).items
        ? [...(bill as any).items].sort((x: any, y: any) => (x.order_number ?? 0) - (y.order_number ?? 0)).map(billLineFromRow)
        : (defaults?.lines ?? []).map((line) => ({
            ...emptyBillLine(line.cost_code_id ?? ''),
            name: line.description,
            quantity: String(line.quantity),
            unitPrice: String(line.unit_price),
            unit: line.unit ?? '',
            catalogItemId: line.catalog_item_id ?? null,
            sourceLineId: line.purchase_order_item_id ?? null,
            billAmount: (line.bill_amount !== undefined ? line.bill_amount : line.quantity * line.unit_price).toFixed(2),
          }));
      setLines(items);
      initialLinesRef.current = serializeLines(items);

      const openTax = bill?.id ? (bill.tax_pct ? String(bill.tax_pct) : '') : (defaults?.tax_pct ? String(defaults.tax_pct) : '');
      const openOther = bill?.id ? (bill.other_amount ? String(bill.other_amount) : '') : (defaults?.other_amount ? String(defaults.other_amount) : '');
      setTaxPct(openTax);
      setOtherAmount(openOther);
      // a bill that starts from a purchase order opens with these values already in place: that alone is not a change
      initialChargesRef.current = `${openTax}|${openOther}`;
      setBillingByPo({});
      setBillingError(null);
      setSplitAmount('');
      setSplitError(null);
      // a bill made from an order opens billing everything pending (lines, tax and other); one that already exists is edited by hand
      setBillingMode(!bill?.id && defaults?.purchase_orders?.length ? 'full' : 'manual');

      // a file staged before a cancelled create must not still be waiting the next time this opens
      resetPendingFiles();
      setUploadedAttachments(bill?.id && Array.isArray(bill.attachments) ? (bill.attachments as GenericAttachment[]) : []);
    }
    previousIsOpenRef.current = isOpen;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // The candidates: the purchase orders of the bill's project (the server returns only the caller's company's) -- loaded when the project is known / changes
  const billProjectId: string = formData.project_id || '';
  useEffect(() => {
    if (!isOpen || !billProjectId) {
      setProjectPoOptions([]);
      return undefined;
    }
    let cancelled = false;
    fetchEntityList<any>('purchase_order', { projectId: billProjectId })
      .then((orders) => { if (!cancelled) setProjectPoOptions(orders.map((p: any) => ({ id: p.id, label: poLabel(p), detail: poDetail(p) }))); })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : 'The purchase orders could not be loaded'); });
    return () => { cancelled = true; };
  }, [isOpen, billProjectId]);

  // What the dropdown offers: the project's orders, plus the one already linked when it is not in that list (never silently dropped)
  const poOptions = useMemo(() => {
    const seen = new Set(projectPoOptions.map((o) => o.id));
    return [...projectPoOptions, ...linkedPoOptions.filter((o) => linkedPoIds.includes(o.id) && !seen.has(o.id))];
  }, [projectPoOptions, linkedPoOptions, linkedPoIds]);

  // Picking an order links the bill to it (saved through replaceBillPurchaseOrders); "Select" removes the link. A bill linked to several keeps them (the dropdown is disabled)
  const handlePoChange = (id: string) => {
    const option = poOptions.find((o) => o.id === id);
    setLinkedPoIds(option ? [option.id] : []);
    setLinkedPoOptions(option ? [option] : []);
  };

  // Another project: its orders are other orders -- a selection from the previous project would link the bill across projects
  const handleProjectChange = (value: string) => {
    if (value !== formData.project_id && lockedPoIds.length === 0) {
      setLinkedPoIds([]);
      setLinkedPoOptions([]);
    }
    handleChange('project_id', value);
  };

  // What has been paid on the bill (server-computed): loaded when an existing bill opens and after each payment recorded here.
  const refreshBalance = useCallback(async () => {
    if (!activeBillId) return;
    try {
      const fresh = await BillsService.getBillWithBalance(activeBillId);
      setBalance({ amountPaid: Number(fresh.amount_paid) || 0, status: fresh.status });
      setFormData((prev) => (prev.status === fresh.status ? prev : { ...prev, status: fresh.status }));
      initialValuesRef.current = { ...initialValuesRef.current, status: String(fresh.status) };
    } catch (e) {
      console.error('Error loading the bill balance:', e);
    }
  }, [activeBillId]);
  useEffect(() => {
    if (isOpen && activeBillId) void refreshBalance();
  }, [isOpen, activeBillId, refreshBalance]);

  // Subtotal (the Bill Amounts) + Tax + Other = Total: the same arithmetic the server repeats. A bill is what the vendor charged -- no markup.
  const totals = documentTotals(billLinesTotal(lines), taxPct, otherAmount);
  const total = totals.total;

  const handleChange = (name: string, value: any) => setFormData((prev) => ({ ...prev, [name]: value }));

  const handleLinesChange = (next: LineItem[]) => { setBillingMode('manual'); setLines((previous) => syncBillAmounts(previous, next)); };
  const pending = useMemo(() => pendingByLine(billingByPo), [billingByPo]);
  const base = useMemo(() => billingBase(billingByPo), [billingByPo]);

  const linkedPoChanged = () => JSON.stringify([...linkedPoIds].sort()) !== JSON.stringify([...initialLinkedPoRef.current].sort());

  // unsaved changes: against what the form opened with (a bill created from a purchase order opens linked to it: that alone is not a change)
  const linkedPoDirty = () => {
    const opened = isEdit ? initialLinkedPoRef.current : (defaults?.purchase_orders ?? []).map((p) => p.id);
    return JSON.stringify([...linkedPoIds].sort()) !== JSON.stringify([...opened].sort());
  };

  const isDirty = () => {
    const formChanged = JSON.stringify(normalize(formData)) !== JSON.stringify(initialValuesRef.current);
    const itemsChanged = serializeLines(lines) !== initialLinesRef.current;
    return formChanged || itemsChanged || `${taxPct.trim()}|${otherAmount.trim()}` !== initialChargesRef.current.trim() || pendingFiles.length > 0 || !!paymentDraft || linkedPoDirty();
  };

  // Closing / reloading the browser tab with an open, modified form: the browser's own "leave site?" prompt.
  const dirtyNow = isOpen && isDirty();
  useEffect(() => {
    if (!dirtyNow) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirtyNow]);

  const handleCloseAttempt = () => {
    if (!isDirty() || window.confirm('Are you sure you want to lose all unsaved progress?')) onClose();
  };

  // What has been billed of each purchase order this bill is (or is about to be) linked to, read again whenever the set of orders changes.
  // The bill being edited is left out: its own amounts are what is being decided.
  const billingKey = linkedPoIds.slice().sort().join(',');
  useEffect(() => {
    if (!isOpen || linkedPoIds.length === 0) { setBillingByPo({}); return undefined; }
    let cancelled = false;
    Promise.all(linkedPoIds.map((id) => fetchPurchaseOrderBilling(id, activeBillId).then((result) => [id, result] as const)))
      .then((entries) => { if (!cancelled) { setBillingByPo(Object.fromEntries(entries)); setBillingError(null); } })
      .catch((failure) => { if (!cancelled) { console.error('Error loading what has been billed of the purchase orders:', failure); setBillingError('What has been billed of the linked purchase orders could not be loaded: Full Amount and Split proportionally are not available.'); } });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, billingKey, activeBillId]);

  useEffect(() => {
    if (!catalogOpen || catalogVendors !== null) return;
    fetchTeamMemberLookup()
      .then((members) => setCatalogVendors(members.filter((m) => m.role === 'vendor').map((m) => ({ id: m.id, name: m.company_name || `${m.first_name ?? ''} ${m.last_name ?? ''}`.trim() }))))
      .catch((failure) => { console.error('Error loading the vendors:', failure); setCatalogVendors([]); });
  }, [catalogOpen, catalogVendors]);

  const loadPayments = useCallback(async () => {
    if (!activeBillId) { setPayments([]); return; }
    try {
      const rows = await fetchEntityList<any>('bill_payment', { filters: { bill_id: activeBillId } });
      setPayments(toPaymentHistoryEntries(rows, 'bill_id', activeBillId));
    } catch (failure) {
      console.error('Error loading the payments of the bill:', failure);
    }
  }, [activeBillId]);
  useEffect(() => { if (isOpen) void loadPayments(); }, [isOpen, loadPayments]);

  const hasBilling = Object.keys(billingByPo).length > 0;
  const coversOrderLines = lines.some((line) => line.sourceLineId);

  const searchItems = async (term: string) => (await searchCatalog({ search: term, limit: 8 })).rows.map((row) => suggestionOf(row, formData.vendor_id || null));
  // A catalog item picked for a line: the item's name, cost code (else the company default), unit and the vendor's price; the Bill Amount follows quantity x price.
  const lineFromItem = (item: CatalogSearchRow, key?: string, quantity = '1'): LineItem => {
    const picked = lineFromCatalog(item, formData.vendor_id || null, quantity, defaultCostCodeId);
    return { ...picked, key: key ?? picked.key, vendorId: null, priceSource: null, billAmount: lineAmount(picked).toFixed(2) };
  };
  const pickCatalogItem = (lineKey: string, item: CatalogSearchRow) => setLines((previous) => previous.map((line) => (line.key === lineKey ? lineFromItem(item, line.key, line.quantity) : line)));
  const addCatalogItems = (items: CatalogSearchRow[]) => setLines((previous) => [...previous, ...items.map((item) => lineFromItem(item))]);

  const applyShape = (shape: BilledShape) => { setLines(shape.lines); setTaxPct(shape.taxPct); setOtherAmount(shape.otherAmount); };
  // Full Amount: everything still pending of the linked orders -- the lines, the tax and the other charges not billed by other bills
  const chooseFull = () => { setBillingMode('full'); setSplitError(null); applyShape(applyFullAmount(lines, base)); };
  const chooseSplit = () => { setBillingMode('split'); setSplitError(null); setSplitAmount(total.toFixed(2)); };
  const chooseManual = () => { setBillingMode('manual'); setSplitError(null); };
  // Split proportionally: the Amount typed is the TOTAL of the bill (lines + tax + other) and is spread over what is pending of each
  const applySplit = () => {
    const result = splitProportionally(lines, base, splitAmount);
    if (!result.ok) { setSplitError(result.error); return; }
    applyShape(result);
    setSplitError(null);
  };
  const pendingTotal = pendingComponents(lines, base).total / 100;

  const requiredFields: GenericFormField[] = [{ name: 'project_id', label: 'Project', type: 'select', required: true }];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // a ref, not state: a second click in the same tick must find the lock already closed
    if (submitLock.current) return;
    const missing = firstMissingRequiredMessage(requiredFields, formData);
    if (missing) {
      setError(missing);
      return;
    }
    // every line that is going to be saved has a cost code (the shared item-table rule); lines nobody touched are not resent, so an old bill can still be edited
    const lineCodeProblem = !bill?.id || serializeLines(lines) !== initialLinesRef.current ? lineCostCodeProblem(lines) : null;
    if (lineCodeProblem) { setError(lineCodeProblem); return; }
    if (parsePercent(taxPct) === null) { setError('Tax must be a percentage between 0 and 100.'); return; }
    if (otherAmount.trim() !== '' && parseNonNegative(otherAmount) === null) { setError('Other amount must be a number (0 or more).'); return; }
    // a Bill Amount above what is still to bill on its order line is allowed (invoices differ from orders) but never by accident: the user confirms it
    const over = overBilledLines(lines, pending);
    if (over.length > 0 && !window.confirm(`These lines bill more than what is still to bill on their purchase order line: ${over.map((l) => l.name || 'an item without a name').join(', ')}. Save anyway?`)) return;
    // the vendor must belong to the project: asked once, now that the bill is being saved
    if (!(await ensureMembers(formData.project_id, [{ id: formData.vendor_id || '', name: formData.vendor_name || 'The vendor' }]))) return;
    submitLock.current = true;
    setIsSubmitting(true);
    setError(null);
    uploadFailures.current = [];

    try {
      const data = formData;
      const billData: Record<string, any> = {
        project_id: data.project_id,
        number: data.bill_number?.trim() || null,
        name: data.name || null,
        vendor_id: data.vendor_id || null,
        cost_code_id: data.cost_code_id || null,
        type: data.type || 'Bill',
        issue_date: data.issue_date || null,
        due_date: data.due_date || data.issue_date || null,
        total_amount: total,
        notes: data.description || null,
        description: data.description || null,
        tax_pct: parsePercent(taxPct) ?? 0,
        other_amount: parseNonNegative(otherAmount) ?? 0,
      };
      // paid / partially paid come from the payments: only Draft / Open / Void are ever sent
      if (!statusLocked) billData.status = data.status || 'open';
      // A NEW bill says which purchase order(s) it is made from in the same request: the server checks them, links the bill to them and, when no Description is
      // sent, starts it from the order. (Saving still re-sends the set after the bill exists - a no-op when it is already linked.)
      if (!isEdit && linkedPoIds.length > 0) billData.purchase_order_ids = linkedPoIds;

      const savedBill = await onSave(isEdit ? { id: activeBillId, ...billData } : billData);
      if (!savedBill) throw new Error('The bill could not be saved');
      if (!isEdit) setCreatedBillId(savedBill.id);

      // The lines and the files only need the bill to exist: both go at once, each in as few requests as possible.
      const itemsChanged = !bill?.id || serializeLines(lines) !== initialLinesRef.current;
      const billLines: BillLine[] = billLinePayload(lines) as unknown as BillLine[];
      await Promise.all([
        itemsChanged && (billLines.length > 0 || bill?.id || createdBillId) ? BillsService.replaceBillLines(savedBill.id, billLines) : Promise.resolve(),
        uploadPending(savedBill.id),
      ]);

      // The purchase orders: one request, only when they changed (or a new bill starts with some). The bill is already saved, so a
      // failure here keeps the form open on that bill -- saving again retries only this (replacing the set is idempotent).
      if (linkedPoChanged()) {
        try {
          await replaceBillPurchaseOrders(savedBill.id, linkedPoIds);
          initialLinkedPoRef.current = linkedPoIds;
          setLockedPoIds(linkedPoIds);
        } catch (linkError: any) {
          onSaved?.();
          setError(`The bill was saved, but its purchase orders could not be linked: ${linkError?.message || 'unknown error'}\nPress Save Changes to try again.`);
          return;
        }
      }

      // The payment prepared on a new bill: only now that the bill exists. Its own idempotency key makes a retry safe, and a
      // failure here never loses the bill (saved) or the draft (kept).
      if (paymentDraft) {
        try {
          await saveBillPayment({
            project_id: formData.project_id,
            bill_id: savedBill.id,
            vendor_id: paymentDraft.vendor_id || formData.vendor_id || null,
            payment_number: paymentDraft.payment_number || null,
            payment_date: paymentDraft.payment_date,
            status: paymentDraft.status,
            amount: paymentDraft.amount,
            notes: paymentDraft.notes || null,
            client_request_id: paymentDraft.client_request_id,
          }, { companyId: companyId as string, userId: user?.id as string });
          setPaymentDraft(null);
        } catch (paymentError: any) {
          onSaved?.();
          setError(`The bill was saved, but its payment was NOT recorded: ${paymentError?.message || 'unknown error'}\nYour payment is still here: fix it with "Edit payment" or press Save Changes to try again (it will not be duplicated).`);
          return;
        }
      }

      if (uploadFailures.current.length > 0) {
        // the bill and its lines are saved; say which file did not make it (the form is now editing the saved bill: add it again)
        setError(`The bill was saved, but some files could not be attached:\n${uploadFailures.current.join('\n')}\nYou can add them again below.`);
        onSaved?.();
        return;
      }
      onSaved?.();
      onClose();
    } catch (err: any) {
      console.error('Error saving bill:', err);
      setError(err?.message || 'Failed to save bill');
    } finally {
      submitLock.current = false;
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const mode = isEdit ? 'edit' : 'create';
  const statusOptions = statusLocked
    ? [{ label: STATUS_LABEL[liveStatus as string], value: liveStatus as string }]
    : [{ label: 'Draft', value: 'draft' }, { label: 'Open', value: 'open' }, { label: 'Void', value: 'void' }];

  const projectName = projects.find((p) => p.id === formData.project_id)?.name;
  const paidSoFar = balance?.amountPaid ?? 0;
  const savedTotal = bill?.id ? Number(bill.total_amount) || 0 : total;
  const balanceReady = !isEdit || balance !== null; // an existing bill's balance is known before a payment is suggested
  const recordPaymentAllowed = balanceReady && (liveStatus || formData.status) !== 'void' && !!formData.project_id && (isEdit ? savedTotal - paidSoFar > 0 : total > 0);
  const recordPaymentHint = !formData.project_id ? 'Select a project first' : (liveStatus || formData.status) === 'void' ? 'A void bill cannot take payments' : !balanceReady ? 'Loading the bill balance...' : !recordPaymentAllowed ? (isEdit ? 'Nothing left to pay' : 'Add line items first') : undefined;
  const billContext = {
    // while a payment draft exists the payment stays a draft (edited here, recorded by the next Save with its own key), even after the bill was created
    id: paymentDraft ? undefined : activeBillId,
    label: `${bill?.bill_id ? `${bill.bill_id} - ` : ''}${formData.name || 'Untitled Bill'}${projectName ? ` (${projectName})` : ''}`,
    project_id: formData.project_id,
    vendor_id: formData.vendor_id || '',
    vendor_name: formData.vendor_name || '',
    total: savedTotal,
    amountPaid: paidSoFar,
    status: (liveStatus as string) || formData.status || 'open',
  };

  const layout = (
    <div className="generic-form-overlay" style={{ top: 0, left: 0, right: 0, bottom: 0 }} onClick={(e) => e.target === e.currentTarget && handleCloseAttempt()}>
      <form
        onSubmit={handleSubmit}
        className="generic-form-container"
        style={{ width: 'var(--form-width-default)', maxHeight: 'var(--form-max-height-default)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="generic-form-header generic-form-header-default" style={{ backgroundColor: colors.formHeaderFooter }}>
          <h2 className="font-form-title">{bill?.id ? 'Edit Bill' : 'Create Bill'}</h2>
          <CloseButton onClick={handleCloseAttempt} />
        </div>

        <div className="generic-form-body generic-form-body-default">
          {error && <div className="generic-form-error whitespace-pre-line">{error}</div>}

          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="name" className="font-form-label">Bill Name</Label>
                <Input id="name" value={formData.name || ''} onChange={(e) => handleChange('name', e.target.value)} placeholder="Enter bill name" className={FORM_INPUT} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="project_id" className="font-form-label">Project<RequiredMark required /></Label>
                <GenericSelect
                  id="project_id"
                  value={formData.project_id || ''}
                  onValueChange={handleProjectChange}
                  placeholder="Select"
                  required
                  disabled={lockedPoIds.length > 0}
                  contentClassName="max-h-[18.75rem] overflow-y-auto"
                  options={projects.map((p) => ({ label: p.name, value: p.id }))}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="space-y-2">
                <Label htmlFor="bill_number" className="font-form-label">Bill Number</Label>
                <Input id="bill_number" value={formData.bill_number || ''} onChange={(e) => handleChange('bill_number', e.target.value)} placeholder="Enter bill number" className={FORM_INPUT} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="vendor_name" className="font-form-label">Vendor</Label>
                <VendorField
                  id="vendor_name"
                  value={formData.vendor_name || ''}
                  allowFreeText={false}
                  placeholder="Select vendor"
                  onChange={({ id, name }) => setFormData((prev) => ({ ...prev, vendor_id: id || '', vendor_name: name }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="issue_date" className="font-form-label">Date</Label>
                <DateInput id="issue_date" value={formData.issue_date || ''} onChange={(iso) => handleChange('issue_date', iso)} className={FORM_INPUT} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="due_date" className="font-form-label">Due Date</Label>
                <DateInput id="due_date" value={formData.due_date || ''} onChange={(iso) => handleChange('due_date', iso)} className={FORM_INPUT} />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="space-y-2">
                <Label htmlFor="purchase_order_id" className="font-form-label">PO</Label>
                <GenericSelect
                  id="purchase_order_id"
                  value={linkedPoIds.length === 1 ? linkedPoIds[0] : ''}
                  onValueChange={handlePoChange}
                  placeholder="Select"
                  searchable // typing narrows the project's orders; it also never reports a change on mount, as a plain select does while its options load
                  disabled={!billProjectId || linkedPoIds.length > 1}
                  contentClassName="max-h-[18.75rem] overflow-y-auto"
                  options={poOptions.map((o) => ({ label: o.label, value: o.id }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="type" className="font-form-label">Type</Label>
                <GenericSelect
                  id="type"
                  value={formData.type || 'Bill'}
                  onValueChange={(value) => handleChange('type', value)}
                  placeholder="Select"
                  required
                  options={[{ label: 'Bill', value: 'Bill' }, { label: 'Progress Bill', value: 'Progress Bill' }]}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="status" className="font-form-label">Status</Label>
                <GenericSelect
                  key={statusOptions.map((o) => o.value).join()} /* the options change when a payment moves the bill's status */
                  id="status"
                  value={formData.status || 'open'}
                  onValueChange={(value) => handleChange('status', value)}
                  placeholder="Select"
                  required
                  disabled={statusLocked}
                  options={statusOptions}
                />
                {statusLocked && <p className="text-xs text-gray-500">Follows the payments recorded on this bill.</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="amount" className="font-form-label">Amount ($)</Label>
                <Input id="amount" type="text" value={`$${total.toFixed(2)}`} readOnly disabled className={FORM_INPUT} />
              </div>
            </div>

            <LinkedDocsSection docs={linkedPoOptions.map((o) => ({ id: o.id, type: 'Purchase Order', entityType: 'purchase_order', label: o.label, detail: o.detail }))} emptyText="No linked purchase order" hasUnsavedChanges={isDirty} />

            {isEdit && (
              <PaymentHistorySection payments={payments} testId="bill-payments" />
            )}

            <div className="space-y-2">
              <Label htmlFor="description" className="font-form-label">Description</Label>
              <Textarea id="description" value={formData.description || ''} onChange={(e) => handleChange('description', e.target.value)} placeholder="Enter description" />
            </div>

            {/* Items: the same Line Items Section as a purchase order, with a Bill Amount per line, then Tax and Other */}
            {billingError && <div className="generic-form-error" role="alert">{billingError}</div>}
            <LineItemsSection<CatalogSearchRow>
              lines={lines}
              onChange={handleLinesChange}
              searchItems={searchItems}
              onPickItem={(lineKey, suggestion) => pickCatalogItem(lineKey, suggestion.data)}
              costCodeOptions={operationalCodes(companyCostCodes, lines.map((line) => line.costCodeId))
                
                .map((c) => ({ value: c.id, label: costCodeLabel(c) }))}
              showVendor={false}
              billing={{ pendingOf: (line) => (line.sourceLineId && pending.has(line.sourceLineId) ? pending.get(line.sourceLineId) ?? null : null) }}
              adjustments={{ taxPct, onTaxPctChange: (value) => { setBillingMode('manual'); setTaxPct(value); }, otherAmount, onOtherAmountChange: (value) => { setBillingMode('manual'); setOtherAmount(value); } }}
              toolbar={(
                <>
                  <StandardButton type="button" customVariant="white" onClick={() => setLines((previous) => [...previous, emptyBillLine(defaultCostCodeId)])}>Add Line</StandardButton>
                  <StandardButton type="button" customVariant="white" onClick={() => setCatalogOpen(true)}>Add from Catalog</StandardButton>
                </>
              )}
            />
            {hasBilling && coversOrderLines && (
              // How much of the order is being billed: one of three ways, and the Amount (the bill's total) always in view, compact
              <div className="flex flex-col items-end gap-2" data-testid="billing-panel">
                <div className="flex items-center gap-2" role="group" aria-label="How to set the amount billed">
                  <StandardButton type="button" customVariant={billingMode === 'full' ? 'blue' : 'white'} aria-pressed={billingMode === 'full'} onClick={chooseFull} title="Bill everything still pending: every line, the tax and the other charges">Full Amount</StandardButton>
                  <StandardButton type="button" customVariant={billingMode === 'split' ? 'blue' : 'white'} aria-pressed={billingMode === 'split'} onClick={chooseSplit}>Split proportionally</StandardButton>
                  <StandardButton type="button" customVariant={billingMode === 'manual' ? 'blue' : 'white'} aria-pressed={billingMode === 'manual'} onClick={chooseManual}>Manual</StandardButton>
                </div>
                <div className="flex items-center gap-2">
                  <Label htmlFor="split_amount" className="font-form-label">Amount to bill ($)</Label>
                  {/* a fixed, compact width: the field's own class is 100% wide, so the width lives on its wrapper */}
                  <div className="w-36">
                    <Input
                      id="split_amount"
                      inputMode="decimal"
                      value={billingMode === 'split' ? splitAmount : total.toFixed(2)}
                      readOnly={billingMode !== 'split'}
                      placeholder="0.00"
                      aria-invalid={!!splitError || undefined}
                      onChange={(e) => { setSplitAmount(e.target.value); setSplitError(null); }}
                      onKeyDown={(e) => { if (e.key === 'Enter' && billingMode === 'split') { e.preventDefault(); applySplit(); } }}
                      className="font-form-input generic-form-field text-right"
                    />
                  </div>
                  {billingMode === 'split' && <StandardButton type="button" customVariant="blue" onClick={applySplit}>Apply</StandardButton>}
                </div>
                <p className="text-xs text-gray-500" data-testid="pending-total">Still to bill on the order: {formatMoney(pendingTotal)} (lines, tax and other)</p>
                {splitError && <span className="text-sm text-destructive" role="alert">{splitError}</span>}
              </div>
            )}
            {/* Reconciliation with each linked purchase order: what it asked for, what OTHER bills billed, what this bill bills, what is left (a difference is shown, never hidden) */}
            {Object.values(billingByPo).map((order) => {
              const orderLabel = linkedPoOptions.find((o) => o.id === order.purchase_order.id)?.label ?? 'Purchase order';
              const left = order.reconciliation.difference - total;
              return (
                <p key={order.purchase_order.id} className="text-xs text-gray-600" data-testid={`reconciliation-${order.purchase_order.id}`}>
                  {orderLabel}: order total {formatMoney(order.purchase_order.total)} · billed in other bills {formatMoney(order.reconciliation.billed_total)} · this bill {formatMoney(total)} ·{' '}
                  <span className={left < 0 ? 'text-destructive' : undefined}>{left < 0 ? `billed ${formatMoney(-left)} over the order` : `left to bill ${formatMoney(left)}`}</span>
                </p>
              );
            })}

            <GenericAttachmentsSection
              attachments={uploadedAttachments}
              pendingFiles={pendingFiles}
              onUpload={handleFileUpload}
              onDelete={isEdit ? handleDeleteAttachment : undefined}
              onRemovePending={handleRemovePendingFile}
              projectId={formData.project_id || ''} mode={mode}
              disabled={false}
            />

            <GenericCommentsSection
              comments={comments}
              onAddComment={async (content, audience) => { await addComment(content, audience); }}
              onDeleteComment={async (commentId) => {
                if (!window.confirm('Are you sure you want to delete this comment?')) return;
                await deleteComment(commentId);
              }}
              mode={mode}
              audiences={TEAM_CLIENT_VENDOR_AUDIENCES}
              placeholder={isEdit ? 'Add a comment...' : 'Comments are available after creating the bill'}
            />
          </div>
        </div>

        <div className="generic-form-footer generic-form-footer-default" style={{ backgroundColor: colors.formHeaderFooter }}>
          {paymentDraft && (
            <span className="mr-auto text-sm text-gray-700" role="status">
              Payment of ${paymentDraft.amount.toFixed(2)} pending — recorded when you save the bill
            </span>
          )}
          <CancelButton onClick={handleCloseAttempt}>Cancel</CancelButton>
          <StandardButton type="button" customVariant="blue" onClick={() => setShowPayment(true)} disabled={isSubmitting || !recordPaymentAllowed} title={recordPaymentHint}>
            {paymentDraft ? 'Edit Payment' : 'Record Payment'}
          </StandardButton>
          <SaveChangesButton disabled={isSubmitting}>{isSubmitting ? 'Saving...' : 'Save Changes'}</SaveChangesButton>
        </div>
      </form>
    </div>
  );

  return (
    <>
      {membershipPrompt}
      {createPortal(layout, document.body)}
      {/* a sibling of the Bill form, not inside it: events of a portal bubble through React parents (a submit would save the bill) */}
      <AddFromCatalogDialog isOpen={catalogOpen} onClose={() => setCatalogOpen(false)} vendors={catalogVendors ?? []} onAdd={addCatalogItems} />
      <PaymentForm
        key={`${activeBillId || 'draft'}-${showPayment}`}
        isOpen={showPayment}
        payment={null}
        projects={projects}
        billContext={billContext}
        draft={paymentDraft}
        onDraft={setPaymentDraft}
        onClose={() => setShowPayment(false)}
        onSave={(payload) => saveBillPayment(payload, { companyId: companyId as string, userId: user?.id as string })}
        onSaved={() => { void refreshBalance(); void loadPayments(); onSaved?.(); }}
      />
    </>
  );
}
